import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { BadgeFormaPago, sumarPorMoneda, textoPorMoneda } from "../utils/estados";
import {
  BadgeFase,
  COLOR_METODO,
  FASES_PAGO,
  colorAvatar,
  folioPago,
  montoTextoPago,
} from "../utils/pagosUi";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

const TAMANOS_PAGINA = ["10", "20", "30", "50", "Todas"];

// Bandeja de Pagos (pantalla 1 de 3): aquí se controla qué pagos están por
// pagar, en proceso o procesados. Gestionar y registrar el pago ocurren en sus
// propias pantallas (/pagos/:id y /pagos/:id/registrar).
function Pagos() {
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const puedePagar = usuario?.rol === "pagos" || usuario?.rol === "administrador";
  const primerNombre = usuario?.nombre?.split(" ")[0] || "";
  const hora = new Date().getHours();
  const saludoHora = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";

  const [pagos, setPagos] = useState([]);
  // Facturas a crédito por pagar (se ven en su propia pantalla; aquí solo un
  // resumen). Si falla (tabla aún sin crear), la bandeja funciona igual.
  const [creditos, setCreditos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  // Filtros (se aplican al vuelo sobre lo ya cargado)
  const [filtroFase, setFiltroFase] = useState("por_pagar");
  const [busqueda, setBusqueda] = useState(""); // solicitante, folio o proveedor
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroMetodo, setFiltroMetodo] = useState("");
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");
  const [tamanoPagina, setTamanoPagina] = useState("30");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setCargando(true);
    apiFetch("/api/pagos")
      .then((data) => setPagos(data || []))
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
    apiFetch("/api/pagos/creditos")
      .then((data) => setCreditos(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPagina(1);
  }, [filtroFase, busqueda, filtroProveedor, filtroMetodo, filtroDesde, filtroHasta]);

  // Tarjetas de arriba: cuántos hay en cada etapa y por cuánto dinero (en
  // córdobas), sin importar los filtros de la tabla.
  const resumen = Object.keys(FASES_PAGO).map((fase) => {
    const delaFase = pagos.filter((p) => p.fase === fase);
    return { fase, cantidad: delaFase.length, monto: sumarPorMoneda(delaFase.map((p) => ({ moneda: p.moneda, monto: p.monto_total }))) };
  });

  // Créditos aún sin pagar, y cuántos ya vencieron o vencen en una semana.
  const creditosAbiertos = creditos.filter((c) => c.fase_credito !== "pagado");
  const montoCreditos = sumarPorMoneda(creditosAbiertos.map((c) => ({ moneda: c.moneda, monto: c.monto_total })));
  const creditosUrgentes = creditosAbiertos.filter(
    (c) => c.fase_credito === "vencido" || (c.vence_en && Number(c.dias_restantes) <= 7)
  ).length;

  const termino = busqueda.trim().toLowerCase().replace("#", "");
  // La fecha filtra por la fecha que se ve en la tabla (cuándo se aprobó la compra).
  const fechaDe = (p) => String(p.creado_en || "").slice(0, 10);
  const visibles = pagos.filter((p) => {
    if (filtroFase && p.fase !== filtroFase) return false;
    if (
      termino &&
      !p.solicitante_nombre.toLowerCase().includes(termino) &&
      !p.proveedor_nombre.toLowerCase().includes(termino) &&
      !String(p.solicitud_id).padStart(3, "0").includes(termino)
    ) {
      return false;
    }
    if (filtroProveedor && p.proveedor_nombre !== filtroProveedor) return false;
    if (filtroMetodo && p.metodo !== filtroMetodo) return false;
    const f = fechaDe(p);
    if (filtroDesde && f < filtroDesde) return false;
    if (filtroHasta && f > filtroHasta) return false;
    return true;
  });
  const proveedoresDisponibles = [...new Set(pagos.map((p) => p.proveedor_nombre))].sort((a, b) =>
    a.localeCompare(b, "es")
  );
  const hayFiltros = !!((filtroFase && filtroFase !== "por_pagar") || busqueda || filtroProveedor || filtroMetodo || filtroDesde || filtroHasta);

  const totalPaginas =
    tamanoPagina === "Todas" ? 1 : Math.max(1, Math.ceil(visibles.length / Number(tamanoPagina)));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visiblesPagina =
    tamanoPagina === "Todas"
      ? visibles
      : visibles.slice((paginaSegura - 1) * Number(tamanoPagina), paginaSegura * Number(tamanoPagina));
  const limpiarFiltros = () => {
    setFiltroFase("por_pagar");
    setBusqueda("");
    setFiltroProveedor("");
    setFiltroMetodo("");
    setFiltroDesde("");
    setFiltroHasta("");
  };

  // Qué dice el botón de cada fila según la etapa del pago.
  const accionDe = (p) => {
    if (!puedePagar) return { texto: "Ver detalle", ruta: `/pagos/${p.id}`, principal: false };
    if (p.fase === "por_pagar") return { texto: "Gestionar", ruta: `/pagos/${p.id}`, principal: true };
    if (p.fase === "en_proceso") return { texto: "Continuar", ruta: `/pagos/${p.id}/registrar`, principal: true };
    return { texto: "Ver detalle", ruta: `/pagos/${p.id}/registrar`, principal: false };
  };

  return (
    <div>
      <h2 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>💳 Pagos</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", textAlign: "center" }}>
          Pagos de las compras aprobadas que no son a crédito. Primero se gestiona (se confirma o cambia la
          forma de pago), luego se registra el pago con su comprobante y compras recibe el aviso.
        </p>
      </Ayuda>

      <div className="tarjetas-resumen" style={styles.tarjetas}>
        {resumen.map((r) => {
          const f = FASES_PAGO[r.fase];
          const activa = filtroFase === r.fase;
          return (
            <button
              key={r.fase}
              type="button"
              onClick={() => setFiltroFase(activa ? "" : r.fase)}
              style={{ ...styles.tarjeta, ...(activa ? { borderColor: f.color, boxShadow: `0 0 0 2px ${f.bg}` } : {}) }}
              title={activa ? "Quitar el filtro" : `Ver solo: ${f.texto}`}
            >
              <span style={{ ...styles.tarjetaIcono, color: f.color, background: f.bg }}>{f.icono}</span>
              <span style={{ textAlign: "left" }}>
                <span style={styles.tarjetaTitulo}>{f.texto}</span>
                <span style={styles.tarjetaValor}>{r.cantidad}</span>
                <span style={styles.tarjetaMonto}>{textoPorMoneda(r.monto)}</span>
              </span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => navigate("/pagos/creditos")}
          style={{ ...styles.tarjeta, ...(creditosUrgentes > 0 ? { borderColor: "#e7b9b0" } : {}) }}
          title="Ver las facturas a crédito"
        >
          <span style={{ ...styles.tarjetaIcono, color: "#5b3f8f", background: "#ece1f5" }}>🗓️</span>
          <span style={{ textAlign: "left" }}>
            <span style={styles.tarjetaTitulo}>Crédito por pagar</span>
            <span style={styles.tarjetaValor}>{creditosAbiertos.length}</span>
            <span style={styles.tarjetaMonto}>
              {textoPorMoneda(montoCreditos)}
              {creditosUrgentes > 0 && (
                <span style={styles.alertaCredito}>
                  {" "}
                  · ⚠️ {creditosUrgentes} vence{creditosUrgentes === 1 ? "" : "n"} pronto
                </span>
              )}
            </span>
          </span>
        </button>
      </div>

      <div style={styles.card}>
        <div className="barra-filtros" style={styles.filtros}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <span style={styles.iconoBusqueda}>🔍</span>
            <input
              type="text"
              placeholder="Buscar solicitud, proveedor o solicitante..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ ...styles.input, width: "100%", boxSizing: "border-box", paddingLeft: "1.9rem" }}
            />
          </div>
          <select value={filtroProveedor} onChange={(e) => setFiltroProveedor(e.target.value)} style={styles.input}>
            <option value="">Todos los proveedores</option>
            {proveedoresDisponibles.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <select value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)} style={styles.input}>
            <option value="">Toda forma de pago</option>
            <option value="cheque">Cheque</option>
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="tarjeta">Tarjeta</option>
          </select>
          <select value={filtroFase} onChange={(e) => setFiltroFase(e.target.value)} style={styles.input}>
            <option value="">Todos los estados</option>
            {Object.entries(FASES_PAGO).map(([valor, f]) => (
              <option key={valor} value={valor}>
                {f.texto}
              </option>
            ))}
          </select>
          <label style={styles.campoFecha}>
            Desde
            <input type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} style={styles.input} />
          </label>
          <label style={styles.campoFecha}>
            Hasta
            <input type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} style={styles.input} />
          </label>
          {hayFiltros && (
            <button type="button" onClick={limpiarFiltros} style={styles.botonSecundario}>
              🧹 Limpiar
            </button>
          )}
        </div>

        {error && <p style={{ color: "#a3402f" }}>{error}</p>}

        {cargando ? (
          <Cargando compacto />
        ) : visibles.length === 0 ? (
          <div className="vacio-card" style={styles.vacio}>
            <div style={styles.vacioIcono}>{hayFiltros ? "🔎" : "✅"}</div>
            <p style={styles.vacioEyebrow}>
              {saludoHora}
              {primerNombre ? `, ${primerNombre}` : ""} 👋
            </p>
            <h3 style={{ margin: "0 0 0.4rem", color: "var(--tema-titulo, #0b3c60)" }}>
              {hayFiltros ? "No hay pagos con esos filtros" : "Todavía no hay pagos"}
            </h3>
            <p style={{ margin: 0, color: "#5b6472", fontSize: "0.88rem" }}>
              {hayFiltros
                ? "Prueba quitando algún filtro."
                : "Cuando el gerente apruebe una compra que no es a crédito, aparecerá aquí."}
            </p>
          </div>
        ) : (
          <div className="table-scroll" style={styles.tablaWrap}>
            <table style={styles.tabla}>
              <thead>
                <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                  <th style={styles.thAcento}></th>
                  <th style={styles.th}>Solicitud</th>
                  <th style={styles.th}>Proveedor</th>
                  <th style={styles.th}>Solicitante</th>
                  <th style={styles.th}>Fecha</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Monto aprobado</th>
                  <th style={styles.th}>Forma de pago</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Aprobado por</th>
                  <th style={styles.th}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {visiblesPagina.map((p, i) => {
                  const accion = accionDe(p);
                  return (
                    <tr
                      key={p.id}
                      style={{ borderBottom: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "transparent" }}
                    >
                      <td style={{ ...styles.tdAcento, background: COLOR_METODO[p.metodo] || "#d3d9df" }}></td>
                      <td style={styles.td}>
                        <Link to={`/solicitud/${p.solicitud_id}`} style={styles.linkFolio}>
                          {folioPago(p.solicitud_id)}
                        </Link>
                        {!!p.tiene_urgente && <div style={styles.badgeUrgente}>🔴 Urgente</div>}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ ...styles.avatar, background: colorAvatar(p.proveedor_nombre) }}>
                            {p.proveedor_nombre.charAt(0).toUpperCase()}
                          </span>
                          <span style={{ fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" }}>{p.proveedor_nombre}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ ...styles.avatar, background: colorAvatar(p.solicitante_nombre) }}>
                            {p.solicitante_nombre.charAt(0).toUpperCase()}
                          </span>
                          <div>
                            {p.solicitante_nombre}
                            <div style={styles.sub}>{p.area_nombre}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                        {p.creado_en
                          ? new Date(p.creado_en).toLocaleDateString("es-NI", { day: "2-digit", month: "2-digit", year: "numeric" })
                          : "—"}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {montoTextoPago(p)}
                      </td>
                      <td style={styles.td}>
                        {p.metodo ? (
                          <BadgeFormaPago valor={p.metodo} />
                        ) : (
                          <span style={styles.badgePorDefinir} title="Compras no la definió: se elige al gestionar el pago">
                            Por definir
                          </span>
                        )}
                        {p.cambio_de_medio && (
                          <div style={styles.notaCambio} title={p.motivo_cambio || ""}>
                            Antes: {p.metodo_propuesto}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <BadgeFase fase={p.fase} />
                      </td>
                      <td style={styles.td}>{p.aprobado_por_nombre || "—"}</td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={() => navigate(accion.ruta)}
                          style={accion.principal ? styles.botonPagar : styles.botonSecundario}
                        >
                          {accion.texto}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!cargando && visibles.length > 0 && (
          <div style={styles.pieTabla}>
            <span style={{ fontSize: "0.8rem", color: "#5b6472" }}>
              Mostrando {(paginaSegura - 1) * (tamanoPagina === "Todas" ? visibles.length : Number(tamanoPagina)) + 1}
              {" a "}
              {Math.min(
                paginaSegura * (tamanoPagina === "Todas" ? visibles.length : Number(tamanoPagina)),
                visibles.length
              )}
              {" de "}
              {visibles.length}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {totalPaginas > 1 && (
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <button
                    type="button"
                    disabled={paginaSegura === 1}
                    onClick={() => setPagina(paginaSegura - 1)}
                    style={styles.botonPagina}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPaginas }, (_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setPagina(idx + 1)}
                      style={{ ...styles.botonPagina, ...(paginaSegura === idx + 1 ? styles.botonPaginaActivo : {}) }}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={paginaSegura === totalPaginas}
                    onClick={() => setPagina(paginaSegura + 1)}
                    style={styles.botonPagina}
                  >
                    ›
                  </button>
                </div>
              )}
              <label style={{ fontSize: "0.8rem", color: "#5b6472", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                Por página:
                <select
                  value={tamanoPagina}
                  onChange={(e) => setTamanoPagina(e.target.value)}
                  style={{ ...styles.input, padding: "0.3rem 0.4rem" }}
                >
                  {TAMANOS_PAGINA.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  tarjetas: { display: "flex", flexWrap: "wrap", gap: "0.8rem", marginTop: "1.1rem" },
  tarjeta: {
    flex: "1 1 200px",
    display: "flex",
    alignItems: "center",
    gap: "0.8rem",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.85rem 1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tarjetaIcono: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  tarjetaTitulo: { display: "block", fontSize: "0.74rem", fontWeight: 700, color: "#5b6472", textTransform: "uppercase", letterSpacing: "0.03em" },
  tarjetaValor: { display: "block", fontSize: "1.5rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", lineHeight: 1.15, fontVariantNumeric: "tabular-nums" },
  alertaCredito: { color: "#a3402f", fontWeight: 700 },
  tarjetaMonto: { display: "block", fontSize: "0.76rem", color: "#8993a1", fontVariantNumeric: "tabular-nums" },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginTop: "1.1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  input: { padding: "0.45rem 0.6rem", border: "1px solid #d3d9df", borderRadius: 6, fontFamily: "inherit", fontSize: "0.85rem" },
  iconoBusqueda: { position: "absolute", left: "0.55rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.8rem", pointerEvents: "none" },
  tablaWrap: { border: "1px solid #eef1f3", borderRadius: 10 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" },
  th: { padding: "0.55rem 0.5rem", background: "#eef1f3", border: "1px solid #d3d9df", fontSize: "0.76rem", color: "var(--tema-titulo, #0b3c60)", whiteSpace: "nowrap" },
  thAcento: { padding: 0, width: 4, background: "#eef1f3", border: "none", borderBottom: "1px solid #d3d9df" },
  tdAcento: { padding: 0, width: 4 },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  td: {
    padding: "0.65rem 0.7rem",
    textAlign: "left",
    verticalAlign: "middle",
    borderLeft: "1px solid #e3e8ee",
    borderRight: "1px solid #e3e8ee",
  },
  filtros: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
    alignItems: "flex-end",
    margin: "0 0 1.1rem",
    paddingBottom: "1.1rem",
    borderBottom: "1px solid #eef1f3",
  },
  campoFecha: { display: "flex", flexDirection: "column", gap: "0.2rem", fontSize: "0.8rem" },
  sub: { fontSize: "0.75rem", color: "#8993a1", marginTop: "0.1rem" },
  itemPago: { display: "flex", alignItems: "baseline", gap: "0.5rem", marginTop: "0.3rem", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600 },
  chipItem: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#0f7c8a",
    background: "#d5eef1",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  masProductos: { marginTop: "0.3rem", fontSize: "0.76rem", fontWeight: 700, color: "var(--tema-primario, #2f6690)" },
  cantidadFila: { marginTop: "0.3rem", fontSize: "0.86rem", color: "#3d4a5c", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  notaPendientes: {
    marginTop: "0.45rem",
    padding: "0.35rem 0.5rem",
    background: "#f7f8fa",
    border: "1px dashed #d3d9df",
    borderRadius: 6,
    fontSize: "0.72rem",
    color: "#8993a1",
    lineHeight: 1.35,
  },
  badgePorDefinir: {
    display: "inline-block",
    padding: "0.15rem 0.6rem",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#b85a0b",
    background: "#fde6cf",
    whiteSpace: "nowrap",
  },
  notaCambio: { marginTop: "0.25rem", fontSize: "0.7rem", color: "#a3402f", fontWeight: 600 },
  linkFolio: { color: "var(--tema-primario, #2f6690)", fontWeight: 700, textDecoration: "none" },
  badgeUrgente: {
    display: "inline-block",
    marginTop: "0.15rem",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.05rem 0.5rem",
    whiteSpace: "nowrap",
  },
  botonPagar: {
    background: "#0f7c8a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonSecundario: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  vacio: { textAlign: "center", padding: "2.5rem 1rem", background: "linear-gradient(180deg, #f2fafb 0%, #fff 60%)", borderRadius: 12 },
  vacioIcono: {
    width: 64,
    height: 64,
    margin: "0 auto 0.8rem",
    borderRadius: "50%",
    background: "#d5eef1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.8rem",
  },
  vacioEyebrow: { margin: "0 0 0.25rem", color: "#0f7c8a", fontSize: "0.82rem", fontWeight: 700 },
  pieTabla: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "0.9rem",
  },
  botonPagina: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: 30,
  },
  botonPaginaActivo: { background: "#0f7c8a", borderColor: "#0f7c8a", color: "#fff" },
};

export default Pagos;
